import { generatedSyllableAudio } from '@/content/pronunciation/generated-syllables'
import { pinyinNumberToDisplay, pinyinTextNumberToZhuyin } from './pinyin'
import { zhuyinFinalsByPinyin, zhuyinInitialsByPinyin } from './sounds'

export type PronunciationSoundSample = {
  soundTag: string
  pinyin: string
  zhuyin: string
  samplePinyinNumber: string
  samplePinyin: string
  sampleZhuyin: string
  audioPath?: string
}

export const canonicalSampleSyllables: Record<string, string> = {
  // Initials
  'initial-b': 'bo1',
  'initial-p': 'po1',
  'initial-m': 'mo1',
  'initial-f': 'fo1', // fo1 is available in audio-cmn
  'initial-d': 'de1',
  'initial-t': 'te1',
  'initial-n': 'ne1',
  'initial-l': 'le1',
  'initial-g': 'ge1',
  'initial-k': 'ke1',
  'initial-h': 'he1',
  'initial-j': 'ji1',
  'initial-q': 'qi1',
  'initial-x': 'xi1',
  'initial-zh': 'zhi1',
  'initial-ch': 'chi1',
  'initial-sh': 'shi1',
  'initial-r': 'ri4', // ri4 is available in audio-cmn
  'initial-z': 'zi1',
  'initial-c': 'ci1',
  'initial-s': 'si1',

  // Finals
  'final-a': 'a1',
  'final-o': 'o1',
  'final-e': 'e1',
  'final-i': 'yi1', // Standalone i sound maps to yi1
  'final-u': 'wu1', // Standalone u sound maps to wu1
  'final-ü': 'yu1', // Standalone ü sound maps to yu1
  'final-ai': 'ai1',
  'final-ei': 'ei1',
  'final-ao': 'ao1',
  'final-ou': 'ou1',
  'final-an': 'an1',
  'final-ang': 'ang1',
  'final-en': 'en1',
  'final-eng': 'eng1',
  'final-er': 'er2', // er2 is available in audio-cmn
  'final-ia': 'ya1',
  'final-ie': 'ye1', // ye1 is standard for ie (ㄝ) sound
  'final-iao': 'yao1',
  'final-iu': 'you1',
  'final-ian': 'yan1',
  'final-iang': 'yang1',
  'final-in': 'yin1',
  'final-ing': 'ying1',
  'final-iong': 'yong1',
  'final-ua': 'wa1',
  'final-uo': 'wo1',
  'final-uai': 'wai1',
  'final-ui': 'wei1',
  'final-uan': 'wan1',
  'final-uang': 'wang1',
  'final-un': 'wen1',
  'final-ong': 'weng1',
  'final-üe': 'yue1',
  'final-üan': 'yuan1',
  'final-ün': 'yun1'
}

const sampleCache = new Map<string, PronunciationSoundSample>()

export function getSoundSample(
  soundTag: string
): PronunciationSoundSample | null {
  if (sampleCache.has(soundTag)) {
    return sampleCache.get(soundTag)!
  }

  const samplePinyinNumber = canonicalSampleSyllables[soundTag]
  if (!samplePinyinNumber) return null

  let pinyin = ''
  let zhuyin = ''

  if (soundTag.startsWith('initial-')) {
    pinyin = soundTag.replace('initial-', '')
    zhuyin = zhuyinInitialsByPinyin[pinyin] ?? ''
  } else if (soundTag.startsWith('final-')) {
    pinyin = soundTag.replace('final-', '')
    zhuyin = zhuyinFinalsByPinyin[pinyin] ?? ''
  }

  const audioEntry = generatedSyllableAudio.find(
    (entry) => entry.id === samplePinyinNumber
  )
  const audioPath = audioEntry?.audioPath

  const samplePinyin = pinyinNumberToDisplay(samplePinyinNumber)
  const sampleZhuyin = pinyinTextNumberToZhuyin(samplePinyinNumber)

  const sample: PronunciationSoundSample = {
    soundTag,
    pinyin,
    zhuyin,
    samplePinyinNumber,
    samplePinyin,
    sampleZhuyin,
    audioPath
  }

  sampleCache.set(soundTag, sample)
  return sample
}
